import { Injectable } from "@angular/core";
import { BaseService } from "./base.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { EthAbsData } from "../models/eth-abs-data";
import { catchError, Observable } from "rxjs";
import { CropBioKMaxData } from "../models/crop-bio-k-max-data";
import { MacroNutrientData } from "../models/macro-nutrient-data";
import { MicroNutrientData } from "../models/micro-nutrient-data-";
import { GibbPPMData } from "../models/gibb-ppm-data";
import { FieldContext } from "../models/field-context";
import { FarmFieldLinks, FieldLinkAuditEntry, FmsBlockCandidate, SetIgatorLinksResponse, TapCandidate } from "../models/field-link";
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class SyncService extends BaseService {

    private FMS_URL = environment.syncServiceBaseUrl + "fms";
    private FIELD_URL = environment.syncServiceBaseUrl + "field";

    hasTenantFmsData(tenantId: number): Observable<boolean> {
        const params = new HttpParams().set('tenantId', tenantId.toString());
        return this.httpClient.get<boolean>(this.FMS_URL + '/check', { params });
    }

    constructor(
        private httpClient: HttpClient
    ) {
        super();
    }

    getEthAbsStats(fieldId: String, fromDate: String, toDate: String): Observable<EthAbsData> {
        return this.httpClient.get<EthAbsData>(this.FMS_URL + `/eth-abs-per-ha?fieldId=${fieldId}&fromDate=${fromDate}&toDate=${toDate}`)
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }

    getCropBioKMaxStats(fieldId: String, fromDate: String, toDate: String): Observable<CropBioKMaxData> {
        return this.httpClient.get<CropBioKMaxData>(this.FMS_URL + `/crop-bio-kmax-per-ha?fieldId=${fieldId}&fromDate=${fromDate}&toDate=${toDate}`)
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }

    getMacroNutrientStats(fieldId: String, fromDate: String, toDate: String): Observable<MacroNutrientData> {
        return this.httpClient.get<MacroNutrientData>(this.FMS_URL + `/macro-nutrients-per-ha?fieldId=${fieldId}&fromDate=${fromDate}&toDate=${toDate}`)
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }

    getMicroNutrientStats(fieldId: String, fromDate: String, toDate: String): Observable<MicroNutrientData> {
        return this.httpClient.get<MicroNutrientData>(this.FMS_URL + `/micro-nutrients-per-ha?fieldId=${fieldId}&fromDate=${fromDate}&toDate=${toDate}`)
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }

    getGibbPPM(fieldId: String, fromDate: String, toDate: String): Observable<GibbPPMData> {
        return this.httpClient.get<GibbPPMData>(this.FMS_URL + `/gibb-ppm?fieldId=${fieldId}&fromDate=${fromDate}&toDate=${toDate}`)
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }

    /**
     * Whether the field is linked to FMS and iGator, plus its rest-break date for the crop year.
     */
    getFieldContext(fieldId: String, cropYear: number): Observable<FieldContext> {
        const params = new HttpParams().set('cropYear', cropYear.toString());
        return this.httpClient.get<FieldContext>(`${this.FIELD_URL}/${fieldId}/context`, { params })
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }

    /** Every field on the farm with its FMS block and iGator taps, in one call (maps Data links layer). */
    getFarmFieldLinks(farmId: number): Observable<FarmFieldLinks[]> {
        const params = new HttpParams().set('farmId', farmId.toString());
        return this.httpClient.get<FarmFieldLinks[]>(`${this.FIELD_URL}/links`, { params })
            .pipe(catchError((err) => this.errorHandler(err)));
    }

    getFmsLinkCandidates(): Observable<FmsBlockCandidate[]> {
        return this.httpClient.get<FmsBlockCandidate[]>(`${this.FIELD_URL}/link-candidates/fms`)
            .pipe(catchError((err) => this.errorHandler(err)));
    }

    getIgatorLinkCandidates(): Observable<TapCandidate[]> {
        return this.httpClient.get<TapCandidate[]>(`${this.FIELD_URL}/link-candidates/igator`)
            .pipe(catchError((err) => this.errorHandler(err)));
    }

    /** Null block means the field deliberately has no FMS block. Either way the field becomes manual. */
    setFmsLink(fieldId: String, block: { blockNo: string, fmsFarmId: number } | null): Observable<void> {
        return this.httpClient.put<void>(`${this.FIELD_URL}/${fieldId}/links/fms`, block ?? { blockNo: null, fmsFarmId: null })
            .pipe(catchError((err) => this.errorHandler(err)));
    }

    /** The complete tap list for the field. Taps on other fields are moved here, with their history. */
    setIgatorLinks(fieldId: String, taps: string[]): Observable<SetIgatorLinksResponse> {
        return this.httpClient.put<SetIgatorLinksResponse>(`${this.FIELD_URL}/${fieldId}/links/igator`, { taps })
            .pipe(catchError((err) => this.errorHandler(err)));
    }

    getFieldLinkAudit(fieldId: String): Observable<FieldLinkAuditEntry[]> {
        return this.httpClient.get<FieldLinkAuditEntry[]>(`${this.FIELD_URL}/${fieldId}/links/audit`)
            .pipe(catchError((err) => this.errorHandler(err)));
    }

    getFieldInfo(fieldId: String, cropYear: number): Observable<{ crop: string, cultivar: string }> {
        return this.httpClient.get<{ crop: string, cultivar: string }>(this.FMS_URL + `/field-info?fieldId=${fieldId}&cropYear=${cropYear}`)
            .pipe(
                catchError((err) => {
                    return this.errorHandler(err);
                })
            );
    }
}